import * as React from 'react';
import { useState, useRef, cloneElement, useEffect } from 'react';

/** Material UI Packages. */
import { Popover, IconButton, Typography, Box, CircularProgress } from '@mui/material';

/** Material UI Icons. */
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Internal Components. */
import { readSocketData } from 'shared/lib/fetchSocket';
import { formatOperationDateTime } from 'shared/lib/dateFormatter';

export default function CustomPopover({
    open,
    anchorEl,
    handleClose,
    content,
    handleClick,
    buttonStyle,
    icon,
    iconStyle,
}) {
    return (
        <>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            backgroundColor: 'rgba(0, 58, 82, 0.7)',
                            color: 'white',
                            borderRadius: 2,
                            p: 1,
                        },
                    },
                }}
            >
                <Typography sx={{ whiteSpace: 'pre-line', fontSize: '11px', fontWeight: 500 }}>{content}</Typography>
            </Popover>

            <IconButton
                onClick={handleClick}
                sx={{
                    color: 'white',
                    bgcolor: '#FFC300',
                    borderRadius: '15px',
                    p: '3px',
                    ml: '5px',
                    ...buttonStyle,
                }}
            >
                {icon && cloneElement(icon, { sx: { fontSize: '18px', ...iconStyle } })}
            </IconButton>
        </>
    );
}

export function CustomTextPopover({ open, anchorEl, handleClose, handleClick, content, title, buttonStyle }) {
    return (
        <>
            <IconButton
                onClick={handleClick}
                sx={{
                    color: 'white',
                    borderRadius: '15px',
                    width: '20px',
                    height: '20px',
                    ...buttonStyle,
                }}
            >
                <Typography sx={{ fontSize: 10, fontWeight: 'bold' }}>{title}</Typography>
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            backgroundColor: 'rgba(0, 58, 82, 0.85)',
                            color: 'white',
                            borderRadius: 2,
                            p: 1,
                        },
                    },
                }}
            >
                <Typography sx={{ whiteSpace: 'pre-line', fontSize: 12, fontWeight: 500 }}>{content}</Typography>
            </Popover>
        </>
    );
}

export function CustomHistoryPopover({
    historyList,
    setHistoryList,
    indexRef,
    setIndexRef,
    cursorRef,
    hasPrev,
    setHasPrev,
    open,
    anchorEl,
    handleClose,
    handleClick,
    buttonStyle,
    icon,
    iconStyle,
    rows,
    selectedHouseRow,
}) {
    /** Get socket instance for real-time communication. */
    const dispatch = useDispatch();

    /** Shipment type data. */
    const tabValue = useSelector((state) => state.tabValueForm);

    const [prevDisable, setPrevDisable] = useState(false);
    const [nextDisable, setNextDisable] = useState(false);
    const [loading, setLoading] = useState(true);
    
    const fetchingPrevRef = useRef(false);

    const handlePrev = () => {
        
        if (indexRef < historyList.length - 1) {
            setIndexRef((prev) => prev + 1);
            return;
        }
        if (!hasPrev || cursorRef.current == null || fetchingPrevRef.current) return;

        fetchingPrevRef.current = true;
        setLoading(false);

        const childId = tabValue === 4 ? null : selectedHouseRow.id;
        const sendValue = {
            cursor_id: cursorRef.current,
            parent_id: rows.id,
            child_id: childId,
        };
        readSocketData(
            dispatch,
            tabValue,
            'row',
            'il_status_history',
            '',
            sendValue,
            (receivedData) => {
                const newData = receivedData[0].data;
                if (newData?.id == null) {
                    setHasPrev(false);
                    return;
                }
                cursorRef.current = newData.id;
                setHasPrev(Boolean(receivedData[0].has_prev));
                setHistoryList((prev) => [...prev, newData]);
                setIndexRef((prev) => prev + 1);
            },
            () => {},
            () => {
                fetchingPrevRef.current = false;
                setLoading(true);
            }
        );
    };

    const handleNext = () => {
        if (indexRef > 0) {
            const updatedIndex = indexRef - 1;
            setIndexRef(updatedIndex);
        }
    };

    useEffect(() => {
        setNextDisable(indexRef === 0); // Disable 'Next' if it is the latest.
        setPrevDisable(indexRef >= historyList.length - 1 && !hasPrev);
    }, [hasPrev, indexRef, historyList]);

    /** Render the history header. */
    const renderHistoryHeader = () => (
        <Box
            sx={{
                bgcolor: '#f3f4f3',
                height: '35px',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <Typography sx={{ fontSize: 11, fontWeight: 500 }}>Edit history</Typography>
            <Box display="flex" alignItems="center">
                <IconButton onClick={handlePrev} size="small" disabled={prevDisable} aria-label="Previous history">
                    <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton onClick={handleNext} size="small" disabled={nextDisable} aria-label="Next history">
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>
        </Box>
    );

    return (
        <>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            backgroundColor: '#fff',
                            color: '#3c4043',
                            borderRadius: 2,
                            width: '185px',
                        },
                    },
                }}
            >
                <Box sx={{ minHeight: '100px' }}>
                    {renderHistoryHeader()}
                    {loading ? (
                        <>
                            {!historyList[indexRef]?.start && (
                                <Box
                                    sx={{
                                        bgcolor: '#f3f4f3',
                                        height: '45px',
                                        px: 1,
                                        py: 0.5,
                                    }}
                                >
                                    <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                                        {historyList[indexRef]?.registered_by}
                                    </Typography>
                                    <Typography sx={{ fontSize: '10px' }}>
                                        {formatOperationDateTime(historyList[indexRef]?.modified_on, 'MMM D, h:mm A')}
                                    </Typography>
                                </Box>
                            )}
                            <Box
                                sx={{
                                    p: 1,
                                    height: '160px',
                                    overflowY: 'auto',
                                }}
                            >
                                {historyList[indexRef]?.start ? (
                                    <Typography sx={{ fontSize: 11 }}> No older history found.</Typography>
                                ) : (
                                    <div dangerouslySetInnerHTML={{ __html: historyList[indexRef]?.message }} />
                                )}
                            </Box>
                        </>
                    ) : (
                        <Box
                            sx={{
                                p: 1,
                                height: '180px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                </Box>
            </Popover>
            <IconButton
                onClick={handleClick}
                sx={{
                    color: 'white',
                    bgcolor: '#003a52',
                    borderRadius: '15px',
                    p: '3px',
                    ml: '5px',
                    ...buttonStyle,
                }}
            >
                {icon && cloneElement(icon, { sx: { fontSize: '18px', ...iconStyle } })}
            </IconButton>
        </>
    );
}
